/**
 * §03.7 collision response + §14.4 swept contact tests. Step 10 of the sim order.
 *
 * Custom collision only (spine §8): the spatial hash gives candidates, `sweptCircle` gives the
 * narrow phase, so a fast player can never tunnel through a body at 500-enemy density.
 */

import { STEP } from '../core/loop';
import { cfg } from '../core/config';
import type { Enemy } from './entities';
import type { World } from './world';
import { sweptCircleWrapped, wrapDeltaX, wrapDeltaY, wrapPos } from './wrap';
import { getPlayerRuntime } from './player';
import { applyDamageToPlayer, applyKnockbackToPlayer, contactDamageOf, ENEMY_KNOCK_DECAY } from './damage';

/** §03.7 — positional resolve priority: the player displaces enemies. */
export const PUSH_PRIORITY_PLAYER = 1.0;
export const PUSH_PRIORITY_ENEMY = 0.4;
const PUSH_TOTAL = PUSH_PRIORITY_PLAYER + PUSH_PRIORITY_ENEMY;
/** Share of the overlap each body resolves: the higher-priority body moves less. */
export const PLAYER_PUSH_SHARE = PUSH_PRIORITY_ENEMY / PUSH_TOTAL;
export const ENEMY_PUSH_SHARE = PUSH_PRIORITY_PLAYER / PUSH_TOTAL;

/** A body may damage the player at most once per 0.5 s, matching the mercy window (§03.7). */
export const CONTACT_COOLDOWN_TICKS = 30;

/**
 * Query padding in arena units. The hash is rebuilt at tick start, so enemies may have moved up
 * to one tick of travel since; the pad also covers bodies whose centre sits in a neighbouring cell.
 */
const QUERY_PAD = 2.5;
const SEPARATION_PAD = 3.5;

export function stepCollision(w: World): void {
  decayEnemyKnockback(w);
  const p = w.player;
  if (!p.alive || w.outcome !== 'running') return;
  resolveContacts(w);
  resolveSeparation(w);
  // AMD-24 — no walls. Separation may have pushed the player past the seam; canonicalise.
  wrapPos(p.pos);
}

/** Knockback rides on top of steering velocity and decays at 30 u/s² (§03.7). */
function decayEnemyKnockback(w: World): void {
  const live = w.enemies.liveIndices();
  for (let i = 0; i < live.length; i++) {
    const e = w.enemies.slots[live[i]];
    if (!e.alive) continue;
    const kx = e.knock.x;
    const ky = e.knock.y;
    if (kx === 0 && ky === 0) continue;
    e.pos.x += kx * STEP;
    e.pos.y += ky * STEP;
    const km = Math.sqrt(kx * kx + ky * ky);
    const next = km - ENEMY_KNOCK_DECAY * STEP;
    if (next <= 1e-9) {
      e.knock.x = 0;
      e.knock.y = 0;
    } else {
      e.knock.x = (kx / km) * next;
      e.knock.y = (ky / km) * next;
    }
    w.wrapToArena(e.pos, e.hitbox_radius);
  }
}

/**
 * §14.4 — swept player↔enemy contact along this tick's motion segment. Zero tunneling by
 * construction: the DDA segment query collects every cell the path touches and each candidate
 * is narrow-phased with a swept circle test.
 */
function resolveContacts(w: World): void {
  const p = w.player;
  const rt = getPlayerRuntime(w);
  // True motion of this tick. `p.pos` has been canonicalised, so a seam crossing would read as a
  // 200-unit jump if we subtracted raw; the wrapped delta is the real displacement (AMD-24).
  const dx = wrapDeltaX(p.pos.x - rt.prevX);
  const dy = wrapDeltaY(p.pos.y - rt.prevY);
  const r = p.hitbox_radius;
  // The segment is walked in the nearest-image frame anchored at prevX/prevY; the hash wraps
  // cell indices, so an unwrapped end point is exactly what it wants.
  const n = w.hash.querySegment(rt.prevX, rt.prevY, rt.prevX + dx, rt.prevY + dy, r + QUERY_PAD, w.scratchA, w.scratchA.length);
  if (n === 0) return;
  const stamp = w.nextStamp();
  const invulnerable = w.tick < p.dash.iframes_until || w.tick < p.mercy_until;
  for (let i = 0; i < n; i++) {
    const idx = w.scratchA[i];
    if (w.seenStamp[idx] === stamp) continue;
    w.seenStamp[idx] = stamp;
    const e = w.enemies.slots[idx];
    if (!e.alive || e.death_anim > 0 || !e.targetable || e.faction !== 'enemy') continue;
    const t = sweptCircleWrapped(rt.prevX, rt.prevY, dx, dy, r, e.pos.x, e.pos.y, e.hitbox_radius);
    if (t < 0) continue;
    if (invulnerable) continue;
    if (w.tick < e.contact_cd_until) continue;
    const damage = contactDamageOf(w, e);
    const applied = applyDamageToPlayer(w, damage, e.id, e.def_id);
    e.contact_cd_until = w.tick + CONTACT_COOLDOWN_TICKS;
    if (applied) {
      // §03.7 — 4 u/s impulse away from the contact normal, decaying 40 u/s².
      applyKnockbackToPlayer(w, wrapDeltaX(p.pos.x - e.pos.x), wrapDeltaY(p.pos.y - e.pos.y), cfg.get('player.knockback_impulse'));
      return; // mercy invincibility now blocks every further contact this tick
    }
  }
}

/** Circle-circle positional resolve; no separation at all during dash i-frames (pass-through). */
function resolveSeparation(w: World): void {
  const p = w.player;
  if (w.tick < p.dash.iframes_until) return;
  const r = p.hitbox_radius;
  const n = w.hash.queryCircle(p.pos.x, p.pos.y, r + SEPARATION_PAD, w.scratchB, w.scratchB.length);
  if (n === 0) return;
  const stamp = w.nextStamp();
  for (let i = 0; i < n; i++) {
    const idx = w.scratchB[i];
    if (w.seenStamp[idx] === stamp) continue;
    w.seenStamp[idx] = stamp;
    const e = w.enemies.slots[idx];
    if (!e.alive || e.death_anim > 0 || e.faction !== 'enemy') continue;
    separatePair(w, e, r);
  }
}

function separatePair(w: World, e: Enemy, playerRadius: number): void {
  const p = w.player;
  let nx = wrapDeltaX(p.pos.x - e.pos.x);
  let ny = wrapDeltaY(p.pos.y - e.pos.y);
  const R = playerRadius + e.hitbox_radius;
  let d = Math.sqrt(nx * nx + ny * ny);
  if (d >= R) return;
  if (d < 1e-6) {
    // Perfectly coincident centres: push along +x deterministically (never RNG in sim, §7).
    nx = 1;
    ny = 0;
    d = 1;
  } else {
    nx /= d;
    ny /= d;
  }
  const overlap = R - d;
  p.pos.x += nx * overlap * PLAYER_PUSH_SHARE;
  p.pos.y += ny * overlap * PLAYER_PUSH_SHARE;
  e.pos.x -= nx * overlap * ENEMY_PUSH_SHARE;
  e.pos.y -= ny * overlap * ENEMY_PUSH_SHARE;
  w.wrapToArena(e.pos, e.hitbox_radius);
}
